import { GeoPlaceOption } from "./types";

export type FetchGeoPlacesParams = {
  type?: string;
  parentId?: string;
  name?: string;
  page?: number;
  count?: number;
  signal?: AbortSignal;
};

export type FetchGeoPlacesResult = {
  items: GeoPlaceOption[];
  total: number;
  page: number;
  hasMore: boolean;
};

type GeoPlacesApiItem = {
  id: string | number;
  name: string;
  index?: string;
};

type GeoPlacesApiResponse = {
  count?: number;
  item?: GeoPlacesApiItem[];
  items?: GeoPlacesApiItem[];
  page?: number;
  total?: number;
  totalPage?: number;
};

const mapItems = (raw: GeoPlacesApiItem[] | undefined): GeoPlaceOption[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((item) => ({
    id: String(item.id),
    name: item.name,
    ...(item.index ? { index: item.index } : {}),
  }));
};

export const fetchGeoPlaces = async (
  params: FetchGeoPlacesParams = {},
): Promise<FetchGeoPlacesResult> => {
  const query = new URLSearchParams();

  if (params.type) {
    query.set("type", params.type);
  }

  if (params.parentId) {
    query.set("parentId", params.parentId);
  }

  if (params.name) {
    query.set("name", params.name);
  }

  // API rejects page=0; dealer form never sends page on initial load.
  if (params.page != null && params.page > 0) {
    query.set("page", String(params.page));
  }

  if (params.count != null) {
    query.set("count", String(params.count));
  }

  const queryString = query.toString();
  const url = `/internal-api/geo/places${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    credentials: "include",
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data = (await response.json()) as GeoPlacesApiResponse;
  const items = mapItems(data.items ?? data.item);
  const page = data.page ?? params.page ?? 0;
  const total = data.total ?? items.length;
  const count = params.count ?? items.length;
  const totalPage =
    data.totalPage ?? (count > 0 ? Math.ceil(total / count) : 0);
  const hasMore = totalPage > 0 ? page + 1 < totalPage : false;

  return {
    items,
    total,
    page,
    hasMore,
  };
};
